import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { TilesService } from './tiles.service';
import { Tile, TileId } from '../../shared/models/tile';
import { DeviceId, DeviceType } from '../../shared/models/device';
import { SimpleStructure } from '../../shared/models/structure';

const POLL_INTERVAL_MS = 9000;

/**
 * Uniquely identifies a polling consumer (e.g. a component instance).
 * Using a string lets callers choose a meaningful ID (e.g. a UUID generated
 * once per component instance) without coupling to any framework type.
 */
export type PollConsumerId = string;

@Injectable({
  providedIn: 'root'
})
export class TilePollingService implements OnDestroy {
  /**
   * Outer key: consumer ID (one entry per component instance).
   * Inner key: tile ID → Tile.
   *
   * A tile is polled as long as at least one consumer has registered it.
   * A consumer can only remove tiles it registered itself, so two different
   * components showing the same tile never cancel each other's subscription.
   */
  private consumers = new Map<PollConsumerId, Map<TileId, Tile>>();
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private activeSubscription: Subscription | null = null;

  constructor(private tilesService: TilesService) {}

  ngOnDestroy(): void {
    this.cleanup();
  }

  /**
   * Register interest in a tile on behalf of a consumer.
   * Calling this multiple times for the same (consumerId, tile) pair is safe
   * and simply updates the stored Tile reference.
   */
  startPolling(consumerId: PollConsumerId, tile: Tile): void {
    if (!this.consumers.has(consumerId)) {
      this.consumers.set(consumerId, new Map());
    }
    this.consumers.get(consumerId)!.set(tile.ID, tile);
    this.restartPolling();
  }

  /**
   * Remove interest in a single tile for the given consumer.
   * Other consumers that registered the same tile are unaffected.
   */
  stopPolling(consumerId: PollConsumerId, tileId: TileId): void {
    const consumerTiles = this.consumers.get(consumerId);
    if (!consumerTiles) {
      return;
    }
    consumerTiles.delete(tileId);
    if (consumerTiles.size === 0) {
      this.consumers.delete(consumerId);
    }
    this.restartPolling();
  }

  /**
   * Remove all tiles registered by a consumer in one call.
   * Convenient to call from ngOnDestroy without tracking individual tile IDs.
   */
  stopAllPolling(consumerId: PollConsumerId): void {
    if (this.consumers.delete(consumerId)) {
      this.restartPolling();
    }
  }

  /**
   * Immediately cancel the pending timer and re-fetch all registered tiles.
   * Use after a tile config save to reflect backend-side changes (e.g. transformations)
   * without waiting for the next scheduled poll.
   */
  refresh(): void {
    this.restartPolling();
  }

  private restartPolling(): void {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }

    if (this.activeSubscription) {
      this.activeSubscription.unsubscribe();
      this.activeSubscription = null;
    }

    if (this.consumers.size === 0) {
      return;
    }

    // Small delay to batch rapid changes (e.g., during scrolling)
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      this.fetchAllValues();
    }, 100);
  }

  private fetchAllValues(): void {
    // Collect unique tiles across all consumers
    const uniqueTiles = new Map<TileId, Tile>();
    for (const consumerTiles of this.consumers.values()) {
      for (const [tileId, tile] of consumerTiles) {
        uniqueTiles.set(tileId, tile);
      }
    }

    const tilesByDevice = new Map<DeviceId, TileId[]>();
    const loraWanTileIds = new Set<TileId>();

    for (const tile of uniqueTiles.values()) {
      if ((tile.Structure as SimpleStructure)?.Protocol === DeviceType.LoRaWAN) {
        loraWanTileIds.add(tile.ID);
        continue;
      }
      tile.getDeviceIds()?.forEach(deviceId => {
        if (!tilesByDevice.has(deviceId)) {
          tilesByDevice.set(deviceId, []);
        }
        tilesByDevice.get(deviceId)!.push(tile.ID);
      });
    }

    const groups = [...tilesByDevice.values(), Array.from(loraWanTileIds)];
    const fetches = groups
      .filter(tileIds => tileIds.length > 0)
      .map(tileIds => this.tilesService.fetchTileValues$(tileIds));

    if (fetches.length > 0) {
      this.activeSubscription = forkJoin(fetches).subscribe({
        next: () => {
          this.activeSubscription = null;
          this.scheduleNext();
        },
        error: () => {
          this.activeSubscription = null;
          this.scheduleNext();
        }
      });
    }
  }

  private scheduleNext(): void {
    if (this.pollTimer !== null) {
      return;
    }
    if (this.consumers.size > 0) {
      this.pollTimer = setTimeout(() => {
        this.pollTimer = null;
        this.fetchAllValues();
      }, POLL_INTERVAL_MS);
    }
  }

  private cleanup(): void {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.activeSubscription) {
      this.activeSubscription.unsubscribe();
      this.activeSubscription = null;
    }
    this.consumers.clear();
  }
}
